import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Search, ChevronLeft, ChevronRight, Building2, Users } from 'lucide-react';
import { companiesService, PublicCompanySummary } from 'app/javascript/services/companies';
import { ROUTES } from 'app/javascript/constants/routes';

interface PagyMetadata {
  page: number;
  pages: number;
  count: number;
  next: number | null;
  prev: number | null;
}

export default function CompaniesList() {
  const [companies, setCompanies] = useState<PublicCompanySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagy, setPagy] = useState<PagyMetadata | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchCompanies = useCallback(async (searchTerm: string, pageNum: number) => {
    setLoading(true);
    try {
      const data = await companiesService.listPublic({ search: searchTerm || undefined, page: pageNum });
      setCompanies(data.companies);
      setPagy(data.pagy);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies(search, page);
  }, [page, fetchCompanies]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchCompanies(value, 1);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      <div
        className="fixed inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(90deg, #fbbf24 1px, transparent 1px),
            linear-gradient(180deg, #fbbf24 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12">
          <h1 className="font-bebas text-4xl">Equipes</h1>

          <div className="relative w-full md:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-slate-500" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-slate-700/50 rounded-xl leading-5 bg-slate-900/50 text-slate-300 placeholder-slate-500 focus:outline-none focus:bg-slate-900 focus:ring-1 focus:ring-yellow-400/50 focus:border-yellow-400/50 sm:text-sm transition-all"
              placeholder="Buscar equipe..."
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500">Carregando equipes...</div>
        ) : companies.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            {search.trim() ? 'Nenhuma equipe encontrada.' : 'Nenhuma equipe cadastrada.'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {companies.map((company) => (
                <Link
                  key={company.id}
                  to={ROUTES.COMPANY_DETAILS}
                  params={{ slug: company.slug }}
                  className="group relative flex flex-col p-6 bg-slate-800/40 border border-slate-700/50 rounded-2xl hover:bg-slate-800/60 hover:border-yellow-400/30 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-slate-700/30 border border-slate-600/50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {company.logo_url ? (
                        <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain p-1" />
                      ) : (
                        <Building2 className="w-7 h-7 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-white group-hover:text-yellow-400 transition-colors truncate">
                        {company.name}
                      </h3>
                      {(company.city || company.state) && (
                        <p className="text-xs text-slate-500 truncate">
                          {[company.city, company.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {company.description && (
                    <p className="text-sm text-slate-400 line-clamp-2 mb-4">{company.description}</p>
                  )}

                  <div className="mt-auto flex items-center gap-1.5 text-xs text-slate-500">
                    <Users className="w-3.5 h-3.5" />
                    <span>{company.member_count} {company.member_count === 1 ? 'membro' : 'membros'}</span>
                  </div>
                </Link>
              ))}
            </div>

            {pagy && pagy.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!pagy.prev}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-white hover:border-yellow-400/30 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">Anterior</span>
                </button>

                <span className="px-4 py-2 text-sm text-slate-400">
                  {pagy.page} de {pagy.pages}
                </span>

                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!pagy.next}
                  className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-400 hover:text-white hover:border-yellow-400/30 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <span className="text-sm font-medium hidden sm:inline">Proximo</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}
